import moment from "moment";
import { useDispatch, useSelector } from "react-redux";

import actions from "@/actions";
import { SCHEDULE_DATA_PER_PAGE } from "@/components/list/Schedule/constants";
import { ScheduleResult, SearchInput } from "@/components/list/Schedule/types";
import { ScheduleResultsRequest } from "@/components/list/Schedule/types/api";
import { calculateStartPos } from "@/lib/pager";
import { replaceQueryParams } from "@/lib/urlHelper";
import { RootState } from "@/reducers/types";

export const useResultList = (): {
  search: (input: SearchInput, pageInput?: number) => void;
  prev: () => void;
  next: () => void;
  jump: (page) => void;
} => {
  const dispatch = useDispatch();

  const { requestForResultList, currentPageOfResultList } = useSelector((state: RootState) => state.schedule);

  const toRequest = (input: SearchInput): Omit<ScheduleResultsRequest, "start" | "limit"> => {
    return {
      result_date: moment(input.schedule_date).subtract(1, "day").format("YYYY-MM-DD"),
      category_id: input.category_id,
      area_id: input.area_id,
      device_id: input.device_id,
      manager_user_id: input.manager_user_id,
    };
  };

  const search = (input: SearchInput, pageInput?: number) => {
    const p = pageInput ?? 1;
    replaceQueryParams({ pr: p });
    const params = {
      ...toRequest(input),
      start: calculateStartPos(p, SCHEDULE_DATA_PER_PAGE),
      limit: SCHEDULE_DATA_PER_PAGE,
    };
    dispatch(
      actions.schedule.getScheduleResults(params, p, (res: ScheduleResult) => {
        if (res.list.length === 0 && 0 < res.total_num && res.total_num < params.start) {
          const maxPage = Math.ceil(res.total_num / SCHEDULE_DATA_PER_PAGE);
          const fixedParams = { ...params, start: calculateStartPos(maxPage, SCHEDULE_DATA_PER_PAGE) };
          replaceQueryParams({ pr: maxPage });
          dispatch(actions.schedule.getScheduleResults(fixedParams, maxPage));
        }
      })
    );
  };

  const prev = () => {
    const p = currentPageOfResultList - 1;
    replaceQueryParams({ pr: p });
    dispatch(
      actions.schedule.getScheduleResults(
        {
          ...requestForResultList,
          start: calculateStartPos(p, SCHEDULE_DATA_PER_PAGE),
        },
        p
      )
    );
  };

  const next = () => {
    const p = currentPageOfResultList + 1;
    replaceQueryParams({ pr: p });
    dispatch(
      actions.schedule.getScheduleResults(
        {
          ...requestForResultList,
          start: calculateStartPos(p, SCHEDULE_DATA_PER_PAGE),
        },
        p
      )
    );
  };

  const jump = (page) => {
    replaceQueryParams({ pr: page });
    dispatch(
      actions.schedule.getScheduleResults(
        {
          ...requestForResultList,
          start: calculateStartPos(page, SCHEDULE_DATA_PER_PAGE),
        },
        page
      )
    );
  };

  return { search, prev, next, jump };
};
